import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { LoadingSpinner } from "../../components/ui";
import Button from "../../components/ui/button/Button";
import { getErrorMessage } from "../../utils/axios.utils";
import SubjectService from "../../services/SubjectService";
import ExamCategoryService from "../../services/ExamCategoryService";
import ExamRegistrationService from "../../services/ExamRegistrationService";
import {
  ExamCategory,
  Subject,
  ExamRegistration,
  SubjectRegistration,
} from "../../types/types";
import { SubjectRegistrationService } from "../../services/subject-reg.service";
import Stepper from "../../components/exams/Stepper";
import { RootState, useAppSelector } from "../../redux/store";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
};

const stepVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, x: -50, transition: { duration: 0.3 } },
};

const NewSubjectRegistrations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = localStorage.getItem("lang") || "en";
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { examRegistrationId } = useParams<{ examRegistrationId: string }>();
  const [examRegistration, setExamRegistration] =
    useState<ExamRegistration | null>(null);
  const [examCategories, setExamCategories] = useState<ExamCategory[]>([]);
  const [subjectsByCategory, setSubjectsByCategory] = useState<
    Record<number, Subject[]>
  >({});
  const [formData, setFormData] = useState<Record<string, number>>({}); // "subject_id-category_id" -> student_count
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [existingRegistrations, setExistingRegistrations] = useState<
    SubjectRegistration[]
  >([]);
  const [totalStudentsMap, setTotalStudentsMap] = useState<Record<string, number>>({});

  // Fetch exam registration, categories, subjects, and existing registrations
  const fetchData = useCallback(async () => {
    if (!examRegistrationId) {
      toast.error("No exam selected");
      navigate(`/${lang}/${user?.role}/exams`);
      return;
    }

    setLoading(true);

    try {
      // Fetch exam registration
      const examReg = await ExamRegistrationService.getExamRegistration(
        Number(examRegistrationId)
      );
      setExamRegistration(examReg);

      // Fetch exam categories
      const categories = await ExamCategoryService.getExamCategories(
        examReg.exam_type_id
      );
      setExamCategories(categories);

      // Fetch subjects for each category
      const subjectsMap: Record<number, Subject[]> = {};
      for (const category of categories) {
        const subjects = await SubjectService.getSubjects(category.id);
        subjectsMap[category.id] = subjects;
      }

      setSubjectsByCategory(subjectsMap);

      // Fetch all school exam registrations to check for paired exams
      const allRegistrations = await ExamRegistrationService.getSchoolExamRegistrations();

      // Check for Paired Exam Logic (Duplicate Prevention)
      const currentExamName = examReg.exam_type?.name?.toUpperCase();
      let pairedExamName = "";

      if (currentExamName === "TVE A/L") pairedExamName = "GCE A/L";
      else if (currentExamName === "TVE I/L") pairedExamName = "GCE O/L";

      const hasPairedRegistration = allRegistrations.some(
        (reg) => reg.exam_type?.name?.toUpperCase() === pairedExamName
      );

      // If paired registration exists, filter OUT the "Common" categories from this view
      // forcing the user to register them under the main GCE exam.
      if (hasPairedRegistration && pairedExamName) {
        const filteredCategories = categories.filter((cat) => {
          const isCommon = cat.name.toUpperCase().includes("COMMON");
          // If it's a common category, check if it belongs to the PAIRED exam type
          // If so, exclude it (since it should be managed there)
          // Note: The common category coming from API might be the paired one (due to our backend change)

          // However, our backend now returns paired common categories merged in.
          // We can identify them by checking the category name vs paired name context,
          // or rely on the fact that if it SAYS "GCE ... COMMON" and we are in "TVE ...", it's the paired one.

          if (isCommon && cat.name.toUpperCase().includes(pairedExamName)) {
            return false;
          }
          return true;
        });
        setExamCategories(filteredCategories);
      } else {
        setExamCategories(categories);
      }

      // Fetch existing subject registrations
      const registrations =
        await new SubjectRegistrationService().getSubjectRegistrations(
          Number(examRegistrationId)
        );
      const initialFormData: Record<string, number> = {};
      registrations.data.forEach((reg) => {
        const compositeKey = `${reg.subject_id}-${reg.exam_category_id}`;
        initialFormData[compositeKey] =
          reg.exam_registration?.total_students ?? 0;
      });
      setExistingRegistrations(registrations.data);
      setFormData(initialFormData);

      // Fetch all school exam registrations for cross-referencing totals
      // const allRegistrations = await ExamRegistrationService.getSchoolExamRegistrations(); // Already fetched above
      const studentMap: Record<string, number> = {};
      allRegistrations.forEach((reg) => {
        if (reg.exam_type?.name) {
          studentMap[reg.exam_type.name] = reg.total_students;
        }
      });
      setTotalStudentsMap(studentMap);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate(`/${lang}/${user?.role}/exams`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examRegistrationId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMaxStudents = (categoryName: string) => {
    if (!examRegistration || !examRegistration.exam_type) return 0;

    let total = examRegistration.total_students;
    const currentExamType = examRegistration.exam_type.name;
    const isCommon = categoryName.toUpperCase().includes("COMMON");

    if (isCommon) {
      if (currentExamType === "GCE O/L") {
        total += totalStudentsMap["TVE I/L"] || 0;
      } else if (currentExamType === "TVE I/L") {
        total += totalStudentsMap["GCE O/L"] || 0;
      } else if (currentExamType === "GCE A/L") {
        total += totalStudentsMap["TVE A/L"] || 0;
      } else if (currentExamType === "TVE A/L") {
        total += totalStudentsMap["GCE A/L"] || 0;
      }
    }
    return total;
  };

  // Handle input change
  const handleInputChange = (
    subjectId: number,
    categoryId: number,
    value: string
  ) => {
    const compositeKey = `${subjectId}-${categoryId}`;
    const numValue = value === "" ? 0 : Number(value);
    setFormData((prev) => ({ ...prev, [compositeKey]: numValue }));

    // Validate input
    const categoryName = examCategories.find(c => c.id === categoryId)?.name || "";
    const maxStudents = getMaxStudents(categoryName);

    // Create a temporary errors object
    let newErrors = { ...formErrors };
    delete newErrors[compositeKey];

    if (numValue < 0) {
      newErrors[compositeKey] = t("subjectRegistration.errors.negativeStudentCount");
    } else if (numValue > maxStudents) {
      newErrors[compositeKey] = t("subjectRegistration.errors.exceedsTotalStudents", {
        total: maxStudents,
      });
    }

    // Special Validation for GCE O/L: Lit English + Physics <= Max
    if (examRegistration?.exam_type?.name === "GCE O/L") {
      const findSubject = (p1: string, p2?: string) => {
        for (const [catId, subjects] of Object.entries(subjectsByCategory)) {
          const sub = subjects.find(s => {
            const name = s.name.toLowerCase();
            return name.includes(p1) && (!p2 || name.includes(p2));
          });
          if (sub) return { sub, catId: Number(catId) };
        }
        return null;
      };

      const litEngInfo = findSubject("lit", "english");
      const physicsInfo = findSubject("physics");

      if (litEngInfo && physicsInfo) {
        const isLit = subjectId === litEngInfo.sub.id && categoryId === litEngInfo.catId;
        const isPhy = subjectId === physicsInfo.sub.id && categoryId === physicsInfo.catId;

        if (isLit || isPhy) {
          const litKey = `${litEngInfo.sub.id}-${litEngInfo.catId}`;
          const phyKey = `${physicsInfo.sub.id}-${physicsInfo.catId}`;

          const litCount = isLit ? numValue : (formData[litKey] || 0);
          const phyCount = isPhy ? numValue : (formData[phyKey] || 0);
          const combinedTotal = litCount + phyCount;

          // Check if combined total exceeds max
          // Note: Using the max of the current category, or generally the exam total. 
          // Assuming both share the same limit.
          if (combinedTotal > maxStudents) {
            const errMessage = `Total of ${litEngInfo.sub.name} and ${physicsInfo.sub.name} (${combinedTotal}) cannot exceed limit (${maxStudents})`;
            newErrors[litKey] = errMessage;
            newErrors[phyKey] = errMessage;
          } else {
            // If valid now, we should clear the specific combined error if it existed
            // But be careful not to clear individual "exceeds" errors.
            // Simple approach: if individual check passed (line 174), and this passed, clear error.
            // We need to re-evaluate the *other* field's error state too.

            // Re-validate Lit
            if (litCount <= maxStudents && newErrors[litKey]?.includes("Total of")) {
              delete newErrors[litKey];
            }
            // Re-validate Phy
            if (phyCount <= maxStudents && newErrors[phyKey]?.includes("Total of")) {
              delete newErrors[phyKey];
            }
          }
        }
      }
    }

    // Special Validation for TVE Groups (Commercial & Industrial)
    // Rule: Sum of ALL subjects in (Commercial + Industrial) <= Max Students
    const isTveAlInfo = categoryName.toUpperCase().includes("TVE A/L");
    const isTveIlInfo = categoryName.toUpperCase().includes("TVE I/L");
    const isCommercial = categoryName.toUpperCase().includes("COMMERCIAL");
    const isIndustrial = categoryName.toUpperCase().includes("INDUSTRIAL");

    if ((isTveAlInfo || isTveIlInfo) && (isCommercial || isIndustrial)) {
      const prefix = isTveAlInfo ? "TVE A/L" : "TVE I/L";

      // Find all categories involved in this group (both Commercial and Industrial for this level)
      const groupCategoryIds = examCategories
        .filter(c => {
          const nameU = c.name.toUpperCase();
          return nameU.includes(prefix) && (nameU.includes("COMMERCIAL") || nameU.includes("INDUSTRIAL"));
        })
        .map(c => c.id);

      if (groupCategoryIds.includes(categoryId)) {
        // Calculate Sum of all subjects in these categories
        let groupTotal = 0;

        // We need to iterate over all possible subjects in these categories and check formData
        groupCategoryIds.forEach(catId => {
          const subjects = subjectsByCategory[catId] || [];
          subjects.forEach(sub => {
            const key = `${sub.id}-${catId}`;
            // If this is the field currently being edited, use numValue, else use formData
            if (key === compositeKey) {
              groupTotal += numValue;
            } else {
              groupTotal += (formData[key] || 0);
            }
          });
        });

        if (groupTotal > maxStudents) {
          const errMessage = t("subjectRegistration.errors.exceedsTotalStudents", {
            total: maxStudents,
          }) + ` (Group Total: ${groupTotal})`;

          newErrors[compositeKey] = errMessage;
        }
      }
    }

    setFormErrors(newErrors);
  };

  // Validate current step
  const validateStep = () => {
    const currentCategory = examCategories[currentStep];
    if (!currentCategory) return false;

    // Refresh maxStudents context
    const maxStudents = getMaxStudents(currentCategory.name);

    // Check standard individual limits first
    const subjects = subjectsByCategory[currentCategory.id] || [];
    for (const subject of subjects) {
      const compositeKey = `${subject.id}-${currentCategory.id}`;
      const count = formData[compositeKey] || 0;

      if (count < 0) {
        setFormErrors((prev) => ({
          ...prev,
          [compositeKey]: t("subjectRegistration.errors.negativeStudentCount"),
        }));
        return false;
      }
      if (count > maxStudents) {
        setFormErrors((prev) => ({
          ...prev,
          [compositeKey]: t("subjectRegistration.errors.exceedsTotalStudents", {
            total: maxStudents,
          }),
        }));
        return false;
      }
    }

    // Check TVE Group Limits in validateStep
    // This ensures we catch issues if valid individual edits summed up to invalid group total
    const catName = currentCategory.name.toUpperCase();
    const isTveAl = catName.includes("TVE A/L");
    const isTveIl = catName.includes("TVE I/L");
    const isComm = catName.includes("COMMERCIAL");
    const isInd = catName.includes("INDUSTRIAL");

    if ((isTveAl || isTveIl) && (isComm || isInd)) {
      const prefix = isTveAl ? "TVE A/L" : "TVE I/L";
      const groupCategoryIds = examCategories
        .filter(c => {
          const nameU = c.name.toUpperCase();
          return nameU.includes(prefix) && (nameU.includes("COMMERCIAL") || nameU.includes("INDUSTRIAL"));
        })
        .map(c => c.id);

      // Sum total
      let groupTotal = 0;
      groupCategoryIds.forEach(catId => {
        const subs = subjectsByCategory[catId] || [];
        subs.forEach(sub => {
          groupTotal += (formData[`${sub.id}-${catId}`] || 0);
        });
      });

      if (groupTotal > maxStudents) {
        // Mark error on first field of current step? Or generic error?
        // Ideally we find the last modified field or just all of them.
        // For simplicity, we toast an error and prevent proceed.
        toast.error(`Total students for ${prefix} Commercial & Industrial subjects (${groupTotal}) exceeds limit (${maxStudents})`);
        return false;
      }
    }

    return true;
  };

  // Handle step navigation
  const handleNext = () => {
    if (validateStep() && currentStep < examCategories.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else if (!validateStep()) {
      toast.error(t("subjectRegistration.errors.invalidForm"));
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep()) {
      toast.error(t("subjectRegistration.errors.invalidForm"));
      return;
    }

    setActionLoading(true);
    try {
      // Prepare data for create/update
      const createData = [];
      const updateData = [];
      for (const compositeKey in formData) {
        const studentCount = formData[compositeKey];
        if (studentCount > 0) {
          const [subjectIdStr, categoryIdStr] = compositeKey.split("-");
          const subjectId = Number(subjectIdStr);
          const categoryId = Number(categoryIdStr);
          const existing = existingRegistrations.find(
            (reg) =>
              reg.subject_id === subjectId &&
              reg.exam_category_id === categoryId
          );
          if (existing) {
            updateData.push({ id: existing.id, student_count: studentCount });
          } else {
            createData.push({
              exam_registration_id: Number(examRegistrationId),
              subject_id: subjectId,
              exam_category_id: categoryId,
              student_count: studentCount,
            });
          }
        }
      }

      // Submit creates
      if (createData.length > 0) {
        await new SubjectRegistrationService().createSubjectRegistrations(
          createData
        );
      }

      // Submit updates
      if (updateData.length > 0) {
        await new SubjectRegistrationService().updateSubjectRegistrations(
          updateData
        );
      }

      toast.success(t("subjectRegistration.submitSuccess"));
      navigate(`/${lang}/${user?.role}/exams/subjects`);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(`/${lang}/${user?.role}/exams`);
  };

  return (
    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-4 py-4 min-h-screen">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-3xl font-bold text-gray-900 dark:text-white mb-8"
      >
        {t("subjectRegistration.title")}
      </motion.h1>

      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center h-64"
        >
          <LoadingSpinner size="xl" />
        </motion.div>
      )}

      {!loading && examRegistration && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Stepper
            steps={examCategories.map((cat) => cat.name)}
            currentStep={currentStep}
          />

          <AnimatePresence mode="wait">
            {examCategories[currentStep] && (
              <motion.div
                key={currentStep}
                variants={stepVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
              >
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  {t("subjectRegistration.category", {
                    name: examCategories[currentStep].name,
                  })}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t("subjectRegistration.totalStudents", {
                    count: getMaxStudents(examCategories[currentStep].name),
                  })}
                </p>
                <div className="space-y-4">
                  {(
                    subjectsByCategory[examCategories[currentStep].id] || []
                  ).map((subject) => (
                    <motion.div
                      key={subject.id}
                      variants={itemVariants}
                      className="flex items-center gap-4"
                    >
                      <label
                        htmlFor={`subject-${subject.id}`}
                        className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        {subject.name}
                      </label>
                      <input
                        type="number"
                        id={`subject-${subject.id}-${examCategories[currentStep].id}`}
                        value={
                          formData[
                          `${subject.id}-${examCategories[currentStep].id}`
                          ] || ""
                        }
                        onChange={(e) =>
                          handleInputChange(
                            subject.id,
                            examCategories[currentStep].id,
                            e.target.value
                          )
                        }
                        min="0"
                        max={getMaxStudents(examCategories[currentStep].name)}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 transition-all duration-200"
                        aria-invalid={
                          !!formErrors[
                          `${subject.id}-${examCategories[currentStep].id}`
                          ]
                        }
                        aria-describedby={
                          formErrors[
                            `${subject.id}-${examCategories[currentStep].id}`
                          ]
                            ? `subject-${subject.id}-${examCategories[currentStep].id}-error`
                            : undefined
                        }
                      />
                      {formErrors[
                        `${subject.id}-${examCategories[currentStep].id}`
                      ] && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            id={`subject-${subject.id}-${examCategories[currentStep].id}-error`}
                            className="text-sm text-red-600 dark:text-red-400"
                          >
                            {
                              formErrors[
                              `${subject.id}-${examCategories[currentStep].id}`
                              ]
                            }
                          </motion.p>
                        )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-between gap-4 mt-6"
          >
            <Button
              onClick={handleCancel}
              variant="outline"
              size="md"
              className="w-full sm:w-auto"
            >
              {t("subjectRegistration.cancel")}
            </Button>
            <div className="flex gap-4">
              {currentStep > 0 && (
                <Button
                  onClick={handlePrevious}
                  variant="outline"
                  size="md"
                  className="w-full sm:w-auto"
                >
                  {t("subjectRegistration.previous")}
                </Button>
              )}
              {currentStep < examCategories.length - 1 ? (
                <Button
                  onClick={handleNext}
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  disabled={actionLoading || Object.keys(formErrors).length > 0}
                >
                  {t("subjectRegistration.next")}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  disabled={actionLoading || Object.keys(formErrors).length > 0}
                >
                  {actionLoading
                    ? t("subjectRegistration.saving")
                    : t("subjectRegistration.submit")}
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default NewSubjectRegistrations;
